import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash, faCheck, faXmark } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../themeControl/ThemeProvider';
import { useAlert } from '../../providers/AlertProvider';
import { getCollectionsByDate, createCollection, updateCollection, deleteCollection, bulkUpdateCollections } from '../../api/collections';
import { useSupabase } from '../../providers/SupabaseProvider';
import { useAuthProvider } from '../../providers/AuthProvider';
import logger from "../../utils/logger";

// ─── CollectionRow must live OUTSIDE Collections to prevent focus loss ────────
function CollectionRow({ collection, color, onUpdate, onDelete, bulkEditMode, bulkRowData, onBulkChange, canEdit }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState({
        full_pallets: collection.full_pallets,
        euro_pallets: collection.euro_pallets,
        oversize_pallets: collection.oversize_pallets,
        collected: collection.collected,
        notes: collection.notes || ''
    });

    const handleSave = () => {
        onUpdate(collection.id, editData);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditData({
            full_pallets: collection.full_pallets,
            euro_pallets: collection.euro_pallets,
            oversize_pallets: collection.oversize_pallets,
            collected: collection.collected,
            notes: collection.notes || ''
        });
        setIsEditing(false);
    };

    const activeData = (bulkEditMode && bulkRowData) ? bulkRowData : editData;
    const setActiveField = bulkEditMode
        ? (field, value) => onBulkChange(collection.id, field, value)
        : (field, value) => setEditData(prev => ({ ...prev, [field]: value }));

    const totalPallets = activeData.full_pallets + activeData.euro_pallets + activeData.oversize_pallets;

    return (
        <tr style={{
            borderBottom: `1px solid ${color.border}`,
            backgroundColor: ((bulkEditMode && bulkRowData) ? bulkRowData.collected : collection.collected)
                ? 'rgba(34, 197, 94, 0.1)'
                : 'rgba(239, 68, 68, 0.1)'
        }}>
            <td style={{ padding: '6px 8px', verticalAlign: 'middle', lineHeight: '1.2' }}>
                {collection.customer_name}
            </td>
            <td style={{ padding: '6px 8px', textAlign: 'center', lineHeight: '1.2' }}>
                {(isEditing || bulkEditMode) ? (
                    <input
                        type="number" min="0"
                        value={activeData.full_pallets}
                        onChange={(e) => setActiveField('full_pallets', parseInt(e.target.value) || 0)}
                        style={{ width: '50px', padding: '2px 4px', border: `1px solid ${color.border}`, borderRadius: '4px', backgroundColor: color.bg, color: color.fg, textAlign: 'center', fontSize: '12px' }}
                    />
                ) : collection.full_pallets}
            </td>
            <td style={{ padding: '6px 8px', textAlign: 'center', lineHeight: '1.2' }}>
                {(isEditing || bulkEditMode) ? (
                    <input
                        type="number" min="0"
                        value={activeData.euro_pallets}
                        onChange={(e) => setActiveField('euro_pallets', parseInt(e.target.value) || 0)}
                        style={{ width: '50px', padding: '2px 4px', border: `1px solid ${color.border}`, borderRadius: '4px', backgroundColor: color.bg, color: color.fg, textAlign: 'center', fontSize: '12px' }}
                    />
                ) : collection.euro_pallets}
            </td>
            <td style={{ padding: '6px 8px', textAlign: 'center', lineHeight: '1.2' }}>
                {(isEditing || bulkEditMode) ? (
                    <input
                        type="number" min="0"
                        value={activeData.oversize_pallets}
                        onChange={(e) => setActiveField('oversize_pallets', parseInt(e.target.value) || 0)}
                        style={{ width: '50px', padding: '2px 4px', border: `1px solid ${color.border}`, borderRadius: '4px', backgroundColor: color.bg, color: color.fg, textAlign: 'center', fontSize: '12px' }}
                    />
                ) : collection.oversize_pallets}
            </td>
            <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 'bold', lineHeight: '1.2' }}>
                {(isEditing || bulkEditMode) ? totalPallets : collection.total_pallets}
            </td>
            <td style={{ padding: '6px 8px', textAlign: 'center', lineHeight: '1.2' }}>
                {(isEditing || bulkEditMode) ? (
                    <input
                        type="checkbox"
                        checked={activeData.collected}
                        onChange={(e) => setActiveField('collected', e.target.checked)}
                        style={{ transform: 'scale(1.2)' }}
                    />
                ) : (
                    <span style={{ color: collection.collected ? color.success : color.danger, fontWeight: 'bold' }}>
                        {collection.collected ? '\u2713' : '\u2717'}
                    </span>
                )}
            </td>
            <td style={{ padding: '6px 8px', lineHeight: '1.2' }}>
                {(isEditing || bulkEditMode) ? (
                    <textarea
                        value={activeData.notes}
                        onChange={(e) => setActiveField('notes', e.target.value)}
                        rows={1}
                        style={{ width: '100%', padding: '2px 4px', border: `1px solid ${color.border}`, borderRadius: '4px', backgroundColor: color.bg, color: color.fg, resize: 'vertical', fontSize: '12px', lineHeight: '1.2' }}
                    />
                ) : (
                    collection.notes || '-'
                )}
            </td>
            <td style={{ padding: '6px 8px', textAlign: 'center', lineHeight: '1.2' }}>
                {bulkEditMode ? null : isEditing ? (
                    <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                        <button onClick={handleSave} style={{ padding: '4px 6px', border: 'none', borderRadius: '3px', backgroundColor: color.success, color: 'white', cursor: 'pointer', fontSize: '12px' }}>
                            <FontAwesomeIcon icon={faCheck} style={{ width: 15 }} />
                        </button>
                        <button onClick={handleCancel} style={{ padding: '4px 6px', border: 'none', borderRadius: '3px', backgroundColor: color.border, color: color.fg, cursor: 'pointer', fontSize: '12px' }}>
                            <FontAwesomeIcon icon={faXmark} style={{ width: 15 }} />
                        </button>
                    </div>
                ) : (
                    <div style={{ display: 'flex', gap: '2px', justifyContent: 'center' }}>
                        {canEdit && (
                            <>
                                <button onClick={() => setIsEditing(true)} style={{ padding: '4px 6px', border: 'none', borderRadius: '3px', backgroundColor: color.primary, color: 'white', cursor: 'pointer', fontSize: '12px' }}>
                                    <FontAwesomeIcon icon={faPenToSquare} style={{ width: 15 }} />
                                </button>
                                <button onClick={() => onDelete(collection.id)} style={{ padding: '4px 6px', border: 'none', borderRadius: '3px', backgroundColor: color.danger, color: 'white', cursor: 'pointer', fontSize: '12px' }}>
                                    <FontAwesomeIcon icon={faTrash} style={{ width: 15 }} />
                                </button>
                            </>
                        )}
                        {!canEdit && (
                            <span style={{ fontSize: '11px', color: '#888', fontStyle: 'italic' }}>View-Only</span>
                        )}
                    </div>
                )}
            </td>
        </tr>
    );
}

const Collections = ({ customers = [], profile }) => {
    const { theme } = useTheme();
    const { showAlert } = useAlert();
    const supabase = useSupabase();
    const { isDev, permissions } = useAuthProvider();
    const canEdit = isDev || permissions.perm_collections_edit;
    
    const [selectedDate, setSelectedDate] = useState(() => {
        // Default to today
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [displayDate, setDisplayDate] = useState(() => {
        // Format for display as dd/mm/yyyy
        const today = new Date();
        return today.toLocaleDateString('en-GB'); // This will give dd/mm/yyyy format
    });
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [collections, setCollections] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [editingCollection, setEditingCollection] = useState(null);
    const [realtimeStatus, setRealtimeStatus] = useState('disconnected');
    const [isRealtimeRefreshing, setIsRealtimeRefreshing] = useState(false);

    // ── Bulk edit state ───────────────────────────────────────────────────────
    const [isBulkEditing, setIsBulkEditing] = useState(false);
    const [bulkEditData, setBulkEditData]   = useState({});
    const [isBulkSaving, setIsBulkSaving]   = useState(false);
    const isBulkEditingRef = useRef(false);

    // Keep ref in sync so realtime handler (stale closure) can read current value
    useEffect(() => { isBulkEditingRef.current = isBulkEditing; }, [isBulkEditing]);
    
    // Form state for new/edit collection
    const [collectionForm, setCollectionForm] = useState({
        full_pallets: 0,
        euro_pallets: 0,
        oversize_pallets: 0,
        collected: false,
        notes: ''
    });

    const isLightTheme = theme === 'light';
    
    // Helper functions for date conversion
    const formatDateForDisplay = (isoDate) => {
        try {
            const date = new Date(isoDate + 'T00:00:00');
            return date.toLocaleDateString('en-GB'); // dd/mm/yyyy format
        } catch {
            return isoDate;
        }
    };

    const formatDateForISO = (displayDate) => {
        try {
            const parts = displayDate.split('/');
            if (parts.length === 3) {
                const day = parts[0].padStart(2, '0');
                const month = parts[1].padStart(2, '0');
                const year = parts[2];
                return `${year}-${month}-${day}`;
            }
            return displayDate;
        } catch {
            return displayDate;
        }
    };

    const handleDateChange = (value) => {
        setDisplayDate(value);
        
        // Try to convert to ISO format for API calls
        const dateRegex = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
        const match = value.match(dateRegex);
        
        if (match) {
            const day = parseInt(match[1]);
            const month = parseInt(match[2]);
            const year = parseInt(match[3]);
            
            // Basic validation
            if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100) {
                const isoDate = formatDateForISO(value);
                setSelectedDate(isoDate);
            }
        }
        // Allow incomplete input without validation errors
    };
    
    const color = {
        bg: isLightTheme ? '#ffffff' : '#1a1a1a',
        fg: isLightTheme ? '#333333' : '#ffffff',
        border: isLightTheme ? '#ddd' : '#444',
        panel: isLightTheme ? '#f8f9fa' : '#2a2a2a',
        headerBg: isLightTheme ? '#e9ecef' : '#333',
        success: '#28a745',
        warning: '#ffc107',
        danger: '#dc3545',
        primary: '#007bff'
    };

    // Get unique customers sorted by company name
    const uniqueCustomers = useMemo(() => {
        if (!customers || customers.length === 0) return [];
        
        const uniqueMap = new Map();
        customers.forEach(customer => {
            if (customer.company && !uniqueMap.has(customer.company)) {
                uniqueMap.set(customer.company, customer);
            }
        });
        
        return Array.from(uniqueMap.values())
            .sort((a, b) => a.company.localeCompare(b.company));
    }, [customers]);

    // Fetch collections when date changes
    useEffect(() => {
        if (selectedDate) {
            fetchCollections();
        }
    }, [selectedDate]);

    // Set up realtime subscription for collections table
    useEffect(() => {
        // Check if supabase client is available
        if (!supabase) {
            console.warn('Supabase client not available - realtime updates disabled');
            setRealtimeStatus('unavailable');
            return;
        }

        setRealtimeStatus('connecting');
        
        const collectionsSubscription = supabase
            .channel('collections-live-' + Date.now())
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'public', 
                    table: 'collections'
                },
                (payload) => {
                    // If the change is for the currently selected date, refresh data
                    const changedDate = payload.new?.date || payload.old?.date;
                    
                    if (changedDate === selectedDate && !isBulkEditingRef.current) {
                        setIsRealtimeRefreshing(true);
                        setTimeout(() => {
                            fetchCollections().finally(() => {
                                setTimeout(() => setIsRealtimeRefreshing(false), 500);
                            });
                        }, 100);
                    }
                }
            )
            .subscribe((status, err) => {
                setRealtimeStatus(status.toLowerCase());
                
                if (status === 'SUBSCRIBED') {
                    logger.info('Subscribed to collections realtime updates');
                } else if (status === 'CHANNEL_ERROR') {
                    logger.error('Error subscribing to collections realtime updates');
                }
            });

        return () => {
            setRealtimeStatus('disconnected');
            if (collectionsSubscription) {
                supabase.removeChannel(collectionsSubscription);
            }
        };
    }, [selectedDate, supabase]);

    const fetchCollections = async () => {
        setIsLoading(true);
        try {
            const response = await getCollectionsByDate(selectedDate);
            
            if (response.status === 200) {
                setCollections(response.data || []);
            } else {
                showAlert({
                    message: 'Failed to fetch collections',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error fetching collections:', error);
            showAlert({
                message: 'Error fetching collections',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleAddCollection = async () => {
        if (!selectedCustomerId) {
            showAlert({
                message: 'Please select a customer',
                type: 'warning',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        // Check if collection already exists for this customer and date
        const existingCollection = collections.find(c => c.customer_id === parseInt(selectedCustomerId));
        if (existingCollection) {
            showAlert({
                message: 'A collection already exists for this customer on this date',
                type: 'warning',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            return;
        }

        try {
            const collectionData = {
                customer_id: parseInt(selectedCustomerId),
                date: selectedDate,
                ...collectionForm
            };

            const response = await createCollection(collectionData);
            
            if (response.status === 201) {
                showAlert({
                    message: 'Collection created successfully',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                
                // Reset form and refresh data
                setCollectionForm({
                    full_pallets: 0,
                    euro_pallets: 0,
                    oversize_pallets: 0,
                    collected: false,
                    notes: ''
                });
                setSelectedCustomerId('');
                fetchCollections();
            } else {
                showAlert({
                    message: response.data?.error || 'Failed to create collection',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error creating collection:', error);
            showAlert({
                message: 'Error creating collection',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        }
    };

    const handleUpdateCollection = async (collectionId, updatedData) => {
        try {
            const response = await updateCollection(collectionId, updatedData);
            
            if (response.status === 200) {
                showAlert({
                    message: 'Collection updated successfully',
                    type: 'success',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
                
                fetchCollections();
                setEditingCollection(null);
            } else {
                showAlert({
                    message: response.data?.error || 'Failed to update collection',
                    type: 'danger',
                    buttons: [{ label: 'Close', type: 'primary' }]
                });
            }
        } catch (error) {
            logger.error('Error updating collection:', error);
            showAlert({
                message: 'Error updating collection',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        }
    };

    const handleBulkEditStart = () => {
        const data = {};
        collections.forEach(c => {
            data[c.id] = {
                full_pallets: c.full_pallets,
                euro_pallets: c.euro_pallets,
                oversize_pallets: c.oversize_pallets,
                collected: c.collected,
                notes: c.notes || ''
            };
        });
        setBulkEditData(data);
        setIsBulkEditing(true);
    };

    const handleBulkCancel = () => {
        setIsBulkEditing(false);
        setBulkEditData({});
    };

    const handleBulkChange = (id, field, value) => {
        setBulkEditData(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value }
        }));
    };

    const handleBulkSave = async () => {
        setIsBulkSaving(true);
        const updates = Object.entries(bulkEditData).map(([id, data]) => ({
            id: parseInt(id),
            ...data
        }));
        const result = await bulkUpdateCollections(updates);
        setIsBulkSaving(false);
        if (result.status === 200) {
            showAlert({
                message: `${result.data?.updated ?? updates.length} collection${(result.data?.updated ?? updates.length) !== 1 ? 's' : ''} updated successfully`,
                type: 'success',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
            setIsBulkEditing(false);
            setBulkEditData({});
            fetchCollections();
        } else {
            showAlert({
                message: result.data?.error || 'Failed to save changes',
                type: 'danger',
                buttons: [{ label: 'Close', type: 'primary' }]
            });
        }
    };

    const handleDeleteCollection = async (collectionId) => {
        showAlert({
            message: 'Are you sure you want to delete this collection?',
            type: 'warning',
            buttons: [
                { 
                    label: 'Cancel', 
                    type: 'secondary' 
                },
                { 
                    label: 'Delete', 
                    type: 'danger',
                    onClick: async () => {
                        try {
                            const response = await deleteCollection(collectionId);
                            
                            if (response.status === 200) {
                                showAlert({
                                    message: 'Collection deleted successfully',
                                    type: 'success',
                                    buttons: [{ label: 'Close', type: 'primary' }]
                                });
                                fetchCollections();
                            } else {
                                showAlert({
                                    message: response.data?.error || 'Failed to delete collection',
                                    type: 'danger',
                                    buttons: [{ label: 'Close', type: 'primary' }]
                                });
                            }
                        } catch (error) {
                            logger.error('Error deleting collection:', error);
                            showAlert({
                                message: 'Error deleting collection',
                                type: 'danger',
                                buttons: [{ label: 'Close', type: 'primary' }]
                            });
                        }
                    }
                }
            ]
        });
    };

    return (
        <>
            <style>
                {`
                    @keyframes pulse {
                        0% { opacity: 1; }
                        50% { opacity: 0.5; }
                        100% { opacity: 1; }
                    }
                `}
            </style>
            <div style={{
                fontFamily: 'sans-serif',
                padding: '1rem',
                backgroundColor: color.bg,
                color: color.fg,
                minHeight: '100vh'
            }}>
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto'
            }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '2rem' 
                }}>
                    <div></div> {/* Left spacer */}
                    <h3 style={{ margin: 0, textAlign: 'center' }}>Collections Management</h3>
                    {/* Realtime Status Indicator */}
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        fontSize: '0.875rem',
                        color: realtimeStatus === 'subscribed' && !isRealtimeRefreshing ? color.success : 
                               realtimeStatus === 'channel_error' ? color.danger : 
                               realtimeStatus === 'unavailable' ? color.border :
                               realtimeStatus === 'polling' ? color.primary :
                               color.warning
                    }}>
                        <div 
                            style={{ 
                                width: '8px', 
                                height: '8px', 
                                borderRadius: '50%', 
                                backgroundColor: realtimeStatus === 'subscribed' && !isRealtimeRefreshing ? color.success : 
                                               realtimeStatus === 'channel_error' ? color.danger : 
                                               realtimeStatus === 'unavailable' ? color.border :
                                               realtimeStatus === 'polling' ? color.primary :
                                               color.warning,
                                marginRight: '6px',
                                animation: (realtimeStatus === 'subscribed' && !isRealtimeRefreshing) || realtimeStatus === 'unavailable' ? 'none' : 'pulse 1.5s infinite'
                            }}
                        />
                        {isRealtimeRefreshing ? 'Syncing...' :
                         realtimeStatus === 'subscribed' ? 'Live Updates' : 
                         realtimeStatus === 'channel_error' ? 'Connection Error' : 
                         realtimeStatus === 'unavailable' ? 'Offline Mode' :
                         realtimeStatus === 'polling' ? 'Auto Refresh (5s)' :
                         'Connecting...'}
                    </div>
                </div>

                {/* Date Picker at Top Center */}
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center',
                    marginBottom: '2rem'
                }}>
                    <div style={{ 
                        width: '400px',
                        height: '60px',
                        padding: '0.5rem 1rem',
                        backgroundColor: color.panel,
                        borderRadius: '25px',
                        border: `1px solid ${color.border}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                    }}>
                        <label style={{ fontWeight: 'bold', fontSize: '0.9rem', minWidth: '40px' }}>
                            Date:
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                                type="text"
                                value={displayDate}
                                onChange={(e) => handleDateChange(e.target.value)}
                                placeholder="dd/mm/yyyy"
                                maxLength="10"
                                style={{
                                    width: '120px',
                                    padding: '0.4rem 0.8rem',
                                    border: `1px solid ${color.border}`,
                                    borderRadius: '15px',
                                    backgroundColor: color.bg,
                                    color: color.fg,
                                    fontSize: '0.9rem'
                                }}
                            />
                            <input
                                type="date"
                                value={selectedDate}
                                onChange={(e) => {
                                    setSelectedDate(e.target.value);
                                    setDisplayDate(formatDateForDisplay(e.target.value));
                                }}
                                style={{
                                    position: 'absolute',
                                    opacity: 0,
                                    pointerEvents: 'none',
                                    width: '0',
                                    height: '0'
                                }}
                                ref={(input) => {
                                    if (input) {
                                        window.hiddenDatePicker = input;
                                    }
                                }}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    if (window.hiddenDatePicker) {
                                        window.hiddenDatePicker.style.pointerEvents = 'auto';
                                        window.hiddenDatePicker.style.position = 'static';
                                        window.hiddenDatePicker.style.opacity = '1';
                                        window.hiddenDatePicker.style.width = 'auto';
                                        window.hiddenDatePicker.style.height = 'auto';
                                        window.hiddenDatePicker.showPicker();
                                        window.hiddenDatePicker.style.pointerEvents = 'none';
                                        window.hiddenDatePicker.style.position = 'absolute';
                                        window.hiddenDatePicker.style.opacity = '0';
                                        window.hiddenDatePicker.style.width = '0';
                                        window.hiddenDatePicker.style.height = '0';
                                    }
                                }}
                                style={{
                                    width: '28px',
                                    height: '28px',
                                    border: `1px solid ${color.border}`,
                                    borderRadius: '14px',
                                    backgroundColor: color.primary,
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                📅
                            </button>
                        </div>
                        <small style={{ 
                            color: color.fg, 
                            opacity: 0.6, 
                            fontSize: '0.65rem',
                            marginLeft: '10px',
                            fontStyle: 'italic'
                        }}>
                            dd/mm/yyyy
                        </small>
                    </div>
                </div>

                {/* Form Elements Underneath */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '1rem',
                    marginBottom: '2rem',
                    padding: '1rem',
                    backgroundColor: color.panel,
                    borderRadius: '8px',
                    border: `1px solid ${color.border}`,
                    alignItems: 'end',
                    justifyContent: 'center'
                }}>
                    <div style={{ minWidth: '180px' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Customer:
                        </label>
                        <select
                            value={selectedCustomerId}
                            onChange={(e) => setSelectedCustomerId(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: `1px solid ${color.border}`,
                                borderRadius: '4px',
                                backgroundColor: color.bg,
                                color: color.fg
                            }}
                        >
                            <option value="">Select Customer</option>
                            {uniqueCustomers.map(customer => (
                                <option key={customer.id} value={customer.id}>
                                    {customer.company}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={{ minWidth: '100px' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Full Pallets:
                        </label>
                        <input
                            type="number"
                            min="0"
                            value={collectionForm.full_pallets}
                            onChange={(e) => setCollectionForm(prev => ({ ...prev, full_pallets: parseInt(e.target.value) || 0 }))}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: `1px solid ${color.border}`,
                                borderRadius: '4px',
                                backgroundColor: color.bg,
                                color: color.fg
                            }}
                        />
                    </div>

                    <div style={{ minWidth: '100px' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Euro Pallets:
                        </label>
                        <input
                            type="number"
                            min="0"
                            value={collectionForm.euro_pallets}
                            onChange={(e) => setCollectionForm(prev => ({ ...prev, euro_pallets: parseInt(e.target.value) || 0 }))}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: `1px solid ${color.border}`,
                                borderRadius: '4px',
                                backgroundColor: color.bg,
                                color: color.fg
                            }}
                        />
                    </div>

                    <div style={{ minWidth: '100px' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                            Oversize Pallets:
                        </label>
                        <input
                            type="number"
                            min="0"
                            value={collectionForm.oversize_pallets}
                            onChange={(e) => setCollectionForm(prev => ({ ...prev, oversize_pallets: parseInt(e.target.value) || 0 }))}
                            style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: `1px solid ${color.border}`,
                                borderRadius: '4px',
                                backgroundColor: color.bg,
                                color: color.fg
                            }}
                        />
                    </div>

                    <div style={{ minWidth: '120px' }}>
                        <button
                            onClick={handleAddCollection}
                            disabled={isLoading || !canEdit}
                            style={{
                                width: '100%',
                                padding: '0.5rem 1rem',
                                border: 'none',
                                borderRadius: '4px',
                                backgroundColor: canEdit ? color.success : '#888',
                                color: 'white',
                                cursor: (isLoading || !canEdit) ? 'not-allowed' : 'pointer',
                                fontWeight: 'bold',
                                opacity: (isLoading || !canEdit) ? 0.6 : 1
                            }}
                        >
                            {isLoading ? 'Adding...' : 'Add Collection'}
                        </button>
                    </div>
                </div>

                {/* Collections Table */}
                {(isLoading || isRealtimeRefreshing) ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '2rem',
                        color: color.fg
                    }}>
                        {isRealtimeRefreshing ? 'Syncing latest changes...' : 'Loading collections...'}
                    </div>
                ) : collections.length === 0 ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '2rem',
                        backgroundColor: color.panel,
                        borderRadius: '8px',
                        border: `1px solid ${color.border}`
                    }}>
                        No collections found for {selectedDate}
                    </div>
                ) : (
                    <div style={{
                        backgroundColor: color.panel,
                        borderRadius: '8px',
                        border: `1px solid ${color.border}`,
                        overflow: 'hidden'
                    }}>
                        {/* Bulk edit toolbar */}
                        {canEdit && (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '8px 12px', borderBottom: `1px solid ${color.border}`, backgroundColor: isBulkEditing ? (color.headerBg) : 'transparent' }}>
                                {isBulkEditing ? (
                                    <>
                                        <span style={{ marginRight: 'auto', fontSize: '13px', color: color.fg, opacity: 0.7, alignSelf: 'center' }}>Editing {collections.length} row{collections.length !== 1 ? 's' : ''} &mdash; make changes then save</span>
                                        <button
                                            onClick={handleBulkCancel}
                                            disabled={isBulkSaving}
                                            style={{ padding: '5px 14px', border: `1px solid ${color.border}`, borderRadius: '4px', backgroundColor: color.bg, color: color.fg, cursor: 'pointer', fontSize: '13px' }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleBulkSave}
                                            disabled={isBulkSaving}
                                            style={{ padding: '5px 14px', border: 'none', borderRadius: '4px', backgroundColor: color.success, color: 'white', cursor: isBulkSaving ? 'not-allowed' : 'pointer', fontWeight: 'bold', fontSize: '13px', opacity: isBulkSaving ? 0.7 : 1 }}
                                        >
                                            {isBulkSaving ? 'Saving...' : '💾 Save All Changes'}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={handleBulkEditStart}
                                        style={{ padding: '5px 14px', border: 'none', borderRadius: '4px', backgroundColor: color.primary, color: 'white', cursor: 'pointer', fontSize: '13px' }}
                                    >
                                        ✏️ Edit All Rows
                                    </button>
                                )}
                            </div>
                        )}
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse'
                        }}>
                            <thead style={{
                                backgroundColor: color.headerBg
                            }}>
                                <tr>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: `2px solid ${color.border}` }}>
                                        Customer
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: `2px solid ${color.border}` }}>
                                        Full Pallets
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: `2px solid ${color.border}` }}>
                                        Euro Pallets
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: `2px solid ${color.border}` }}>
                                        Oversize Pallets
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: `2px solid ${color.border}` }}>
                                        Total
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: `2px solid ${color.border}` }}>
                                        Collected
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: `2px solid ${color.border}` }}>
                                        Notes
                                    </th>
                                    <th style={{ padding: '8px', textAlign: 'center', borderBottom: `2px solid ${color.border}` }}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {collections
                                    .sort((a, b) => a.customer_name.localeCompare(b.customer_name))
                                    .map(collection => (
                                    <CollectionRow
                                        key={collection.id}
                                        collection={collection}
                                        color={color}
                                        onUpdate={handleUpdateCollection}
                                        onDelete={handleDeleteCollection}
                                        bulkEditMode={isBulkEditing}
                                        canEdit={canEdit}
                                        bulkRowData={bulkEditData[collection.id]}
                                        onBulkChange={handleBulkChange}
                                    />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
        </>
    );
};

export default Collections;